import {
    computed,
    defineComponent,
    onUnmounted,
    ref,
    watch,
} from "vue";

import {
    getFormattedTime,
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
} from "../../utils.js";

import ImageFallback        from "../../components/imageFallback.js";
import { getDateTime }      from "../../utils/replay.js";
import { VoiceLogTimeline } from "./voiceTimeline.js";
import { VoiceChannelSnapshotService } from "../../projections/voiceChannelSnapshot.js";
import {
    formatVoiceTime,
    readVoiceActorId,
    readVoiceChannelId,
    sameVoiceLog,
} from "./voice/voiceLogIdentity.js";
import { getVoiceActionLabel } from "./voice/voiceLogLabels.js";
import {
    getVoiceSnapshotStateChips,
    getVoiceStateChips,
} from "./voice/voiceStateChips.js";

const Timeline = new VoiceLogTimeline();
const Snapshot = new VoiceChannelSnapshotService();

export default defineComponent({

    props: [ "log", "members", "channels", "onselectLog", "previewMode" ],

    setup(props) {
        const refTimelineLogs    = ref([]);
        const refVoiceSnapshot   = ref(null);
        const refLoading         = ref(false);
        const refSnapshotLoading = ref(false);
        const refLoadingOlder    = ref(false);
        const refLoadingNewer    = ref(false);
        const refError           = ref(null);
        const refChannelId       = ref(null);
        let currentController    = null;

        function abortCurrentLoad() {
            currentController?.abort();
            currentController = null;
        }

        function mergeEntities(result) {
            for (const member of result.members)
                if (member)
                    props.members.value.set(member.id, member);

            for (const channel of result.channels)
                if (channel)
                    props.channels.value.set(channel.id, channel);
        }

        function setTimelineResult(result) {
            mergeEntities(result);
            refTimelineLogs.value = result.logs;
        }

        function mergeVoiceSnapshotEntities(snapshot) {
            for (const [ id, member ] of snapshot.membersPayload ?? [])
                if (id && member)
                    props.members.value.set(id, member);

            for (const member of snapshot.logMembers ?? [])
                if (member)
                    props.members.value.set(member.id, member);

            for (const channel of snapshot.logChannels ?? [])
                if (channel)
                    props.channels.value.set(channel.id, channel);
        }

        async function fetchVoiceSnapshot(log, signal) {
            refSnapshotLoading.value = true;

            try {
                const snapshot = await Snapshot.fetch(log, { signal });

                mergeVoiceSnapshotEntities(snapshot);
                refVoiceSnapshot.value = snapshot;

            } finally {
                if (currentController?.signal === signal)
                    refSnapshotLoading.value = false;
            }
        }

        async function fetchTimeline(log, signal) {
            setTimelineResult(await Timeline.fetchAround(log, { signal }));
        }

        async function loadVoiceContext(log) {
            abortCurrentLoad();

            const controller = new AbortController();

            currentController = controller;
            refLoading.value = true;
            refError.value = null;
            refChannelId.value = Timeline.getChannelId(log);

            try {
                await Promise.all([
                    fetchTimeline(log, controller.signal),
                    fetchVoiceSnapshot(log, controller.signal),
                ]);

            } catch (error) {
                if (error?.name !== "AbortError")
                    refError.value = error.message || "Impossible de charger l'historique vocal.";

            } finally {
                if (currentController === controller) {
                    currentController = null;
                    refLoading.value = false;
                }
            }
        }

        async function loadMore(direction) {
            if (!refChannelId.value)
                return;

            const loadingRef = direction === "older"
                ? refLoadingOlder
                : refLoadingNewer;

            if (loadingRef.value)
                return;

            loadingRef.value = true;
            refError.value = null;

            try {
                const result = direction === "older"
                    ? await Timeline.fetchOlder(refTimelineLogs.value, refChannelId.value)
                    : await Timeline.fetchNewer(refTimelineLogs.value, refChannelId.value);
                const merged = Timeline.mergeResultInto({
                    logs        : refTimelineLogs.value,
                    members     : [],
                    channels    : [],
                }, result);

                setTimelineResult(merged);

            } catch (error) {
                refError.value = error.message || "Impossible de charger plus de logs.";

            } finally {
                loadingRef.value = false;
            }
        }

        function renderUser(userId, compact = false) {
            const member = props.members.value.get(userId);

            return (
                <router-link to={`/users/${userId}/voice`} class={{ "voice-preview-user": true, "voice-preview-user--compact": compact }}>
                    <ImageFallback
                        class="voice-preview-avatar"
                        src={member ? getMemberAvatar(member) : undefined}
                        default="https://cdn.discordapp.com/embed/avatars/0.png"
                        alt={member ? getMemberDisplayName(member) : userId}
                    />
                    <span class="voice-preview-user-text">
                        <span class="voice-preview-displayname">{member ? getMemberDisplayName(member) : "Utilisateur inconnu"}</span>
                        {!compact && <span class="voice-preview-username">{member ? getMemberTag(member) : userId}</span>}
                    </span>
                </router-link>
            );
        }

        function renderChannel(channelId) {
            if (!channelId)
                return <span class="voice-preview-muted">salon inconnu</span>;

            const channel = props.channels.value.get(channelId);

            return (
                <router-link to={`/channels/${channelId}`} class="voice-preview-channel">
                    {channel?.name || channelId}
                </router-link>
            );
        }

        function renderStateChips(log) {
            const chips = getVoiceStateChips(log);

            if (!chips.length)
                return null;

            return (
                <div class="voice-state-chip-list">
                    {chips.map((chip) => (
                        <span class={{ "voice-state-chip": true, "voice-state-chip--active": chip.active }}>
                            <span class="material-symbols-outlined">{chip.icon}</span>
                            {chip.label}
                        </span>
                    ))}
                </div>
            );
        }

        function renderSnapshotStateChips(state) {
            const chips = getVoiceSnapshotStateChips(state);

            if (!chips.length)
                return null;

            return (
                <div class="voice-state-chip-list voice-state-chip-list--session">
                    {chips.map((chip) => (
                        <span class={{ "voice-state-chip": true, "voice-state-chip--active": chip.active }}>
                            <span class="material-symbols-outlined">{chip.icon}</span>
                            {chip.label}
                        </span>
                    ))}
                </div>
            );
        }

        function renderTimelineChannel(log) {
            if (log.type === "voice_move")
                return (
                    <span class="voice-preview-route">
                        {renderChannel(log.from)}
                        <span class="material-symbols-outlined">arrow_forward</span>
                        {renderChannel(log.to)}
                    </span>
                );

            return renderChannel(readVoiceChannelId(log));
        }

        function renderTimelineEntry(log) {
            const actorId = readVoiceActorId(log);
            const selected = sameVoiceLog(log, props.log);

            return (
                <div
                    class={{ "voice-timeline-entry": true, "voice-timeline-entry--selected": selected }}
                    onclick={() => props.onselectLog?.(log)}
                >
                    <div class="voice-timeline-time">{formatVoiceTime(log.createdAt)}</div>
                    <div class="voice-timeline-content">
                        <div class="voice-timeline-line">
                            {actorId ? renderUser(actorId, true) : <span class="voice-preview-muted">Utilisateur inconnu</span>}
                            <span class="voice-timeline-action">{getVoiceActionLabel(log)}</span>
                            {renderTimelineChannel(log)}
                        </div>
                        {renderStateChips(log)}
                    </div>
                </div>
            );
        }

        function renderVoiceSession(entry) {
            const member = props.members.value.get(entry.userId);
            const duration = Math.max(0, props.log.createdAt - entry.joinAt);

            return (
                <router-link
                    to={`/users/${entry.userId}/voice`}
                    class={{ "voice-session-user": true, "voice-session-user--selected": entry.selected }}
                >
                    <ImageFallback
                        class="voice-preview-avatar"
                        src={member ? getMemberAvatar(member) : undefined}
                        default="https://cdn.discordapp.com/embed/avatars/0.png"
                        alt={member ? getMemberDisplayName(member) : entry.userId}
                    />
                    <span class="voice-preview-user-text">
                        <span class="voice-preview-displayname">{member ? getMemberDisplayName(member) : "Utilisateur inconnu"}</span>
                        <span class="voice-preview-username">{member ? getMemberTag(member) : entry.userId}</span>
                    </span>
                    {renderSnapshotStateChips(entry.state)}
                    <span class="voice-session-time">
                        <span class="voice-session-duration">{getFormattedTime(duration)}</span>
                        <span class="voice-session-join-at">{getDateTime(entry.joinAt)}</span>
                    </span>
                </router-link>
            );
        }

        const selectedChannelId = computed(() => readVoiceChannelId(props.log));
        const snapshotMembers = computed(() => refVoiceSnapshot.value?.members ?? []);

        watch(() => props.log, loadVoiceContext, { immediate: true });
        onUnmounted(abortCurrentLoad);

        return () => (
            <div class="log-preview voice-preview">
                <div class="voice-sessions">
                    <div class="voice-sessions-header">
                        <div>
                            <h4 class="voice-sessions-channel-title">
                                <span class="material-symbols-outlined">settings_voice</span>
                                {renderChannel(selectedChannelId.value)}
                            </h4>
                            <p>État du salon à {formatVoiceTime(props.log.createdAt)}</p>
                        </div>
                        {refSnapshotLoading.value && <span class="voice-preview-loader" />}
                    </div>

                    <div class="voice-session-list">
                        {snapshotMembers.value.length
                            ? snapshotMembers.value.map(renderVoiceSession)
                            : !refSnapshotLoading.value && <div class="voice-timeline-empty">Aucune session vocale trouvée.</div>}
                    </div>
                </div>

                <div class="voice-timeline">
                    <div class="voice-timeline-header">
                        <div>
                            <h4>Historique vocal</h4>
                            <p>50 états autour de la log sélectionnée</p>
                        </div>
                        {refLoading.value && <span class="voice-preview-loader" />}
                    </div>

                    <div class="voice-timeline-actions voice-timeline-actions--top">
                        <button type="button" disabled={refLoadingNewer.value || refLoading.value} onclick={() => loadMore("newer")}>
                            {refLoadingNewer.value ? "Chargement…" : "Plus récent"}
                        </button>
                    </div>

                    {refError.value && <div class="voice-timeline-error">{refError.value}</div>}

                    <div class="voice-timeline-list">
                        {refTimelineLogs.value.length
                            ? refTimelineLogs.value.map(renderTimelineEntry)
                            : !refLoading.value && <div class="voice-timeline-empty">Aucun état vocal trouvé.</div>}
                    </div>

                    <div class="voice-timeline-actions">
                        <button type="button" disabled={refLoadingOlder.value || refLoading.value} onclick={() => loadMore("older")}>
                            {refLoadingOlder.value ? "Chargement…" : "Plus ancien"}
                        </button>
                    </div>
                </div>
            </div>
        );
    },
});
